// src/components/Sidebar.js
import {
    Box,
    Image,
    Button,
    IconButton,
    Drawer,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    DrawerBody,
    VStack,
    useDisclosure
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { useEffect, useState } from "react";
import { FaBars } from "react-icons/fa";
import { getSessionPermissions } from "../utils/auth";

const Sidebar = ({ onLogout }) => {
    const navigate = useNavigate();
    const [userLevel, setUserLevel] = useState(null);
    const [userSystemLevel, setUserSystemLevel] = useState(null);
    const [username, setUsername] = useState('');
    const [canViewInfractions, setCanViewInfractions] = useState(false);
    const [canAccessBatches, setCanAccessBatches] = useState(false);
    const { isOpen, onOpen, onClose } = useDisclosure();

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;

        const applyFromToken = () => {
            const decoded = jwtDecode(token);
            const perms = getSessionPermissions(decoded);
            setUserLevel(perms.effectiveRole);
            setUserSystemLevel(decoded.level || 0);
            setUsername(decoded.username || '');
            setCanViewInfractions(perms.canViewInfractions);
            setCanAccessBatches(perms.canAccessBatches);
        };

        applyFromToken();

        // Atualiza poderes a partir do servidor (concede/revoga acesso normal sem novo login)
        fetch('/api/users/me', {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => (res.ok ? res.json() : null))
            .then((data) => {
                if (!data) return;
                const effective = data.effectiveRole ?? data.role ?? 1;
                setUserLevel(effective);
                setCanViewInfractions(Boolean(data.canViewInfractions) || effective >= 2);
                setCanAccessBatches(Boolean(data.canAccessBatches) || effective >= 2);
                if (data.actualRole != null || data.username) {
                    // level/username continuam do token; só reforça menus
                }
            })
            .catch(() => {});
    }, []);

    const navigateTo = (path) => {
        navigate(path);
        onClose();
    };

    const menuButtons = (
        <>
            <Button bg='white' color='teal.500' mb='10px' boxShadow="md" p={4} width={'90%'} onClick={() => navigateTo('/dashboard')}>Home</Button>
            <Button bg='white' color='teal.500' mb='10px' boxShadow="md" p={4} width={'90%'} onClick={() => navigateTo('/passages')}>Passagens</Button>

            {canViewInfractions && (
                <Button bg='white' color='teal.500' mb='10px' boxShadow="md" p={4} width={'90%'} onClick={() => navigateTo('/infractions')}>Infrações</Button>
            )}

            {canAccessBatches && (
                <Button bg='white' color='teal.500' mb='10px' boxShadow="md" p={4} width={'90%'} onClick={() => navigateTo('/batches')}>Lotes</Button>
            )}

            {userLevel >= 4 && (
                <Button bg='white' color='teal.500' mb='10px' boxShadow="md" p={4} width={'90%'} onClick={() => navigateTo('/faces')}>Reconhecimento Facial</Button>
            )}

            {(userSystemLevel >= 6 || username === 'admin') && (
                <>
                    <Button bg='white' color='teal.500' mb='10px' boxShadow="md" p={4} width={'90%'} onClick={() => navigateTo('/map')}>Mapa</Button>
                    <Button bg='white' color='teal.500' mb='10px' boxShadow="md" p={4} width={'90%'} onClick={() => navigateTo('/fiscalization')}>Fiscalização</Button>
                </>
            )}

            {userLevel >= 4 && (
                <>
                    <Button bg='white' color='teal.500' mb='10px' boxShadow="md" p={4} width={'90%'} onClick={() => navigateTo('/machines')}>Máquinas</Button>
                    <Button bg='white' color='teal.500' mb='10px' boxShadow="md" p={4} width={'90%'} onClick={() => navigateTo('/cameras')}>Câmeras</Button>
                    <Button bg='white' color='teal.500' mb='10px' boxShadow="md" p={4} width={'90%'} onClick={() => navigateTo('/algorithms')}>Algoritmos</Button>
                </>
            )}

            {userLevel >= 4 && (
                <>
                    <Button bg='white' color='teal.500' mb='10px' boxShadow="md" p={4} width={'90%'} onClick={() => navigateTo('/users')}>Usuários</Button>
                    <Button bg='white' color='teal.500' mb='10px' boxShadow="md" p={4} width={'90%'} onClick={() => navigateTo('/settings')}>Configurações</Button>
                </>
            )}

            <Button color='red' mt='auto' boxShadow="md" p={4} width={'90%'} onClick={onLogout}>Logout</Button>
        </>
    );

    return (
        <>
            <Box
                display={{ base: "flex", md: "none" }}
                position="fixed"
                top={0}
                left={0}
                right={0}
                h="64px"
                bg="teal.500"
                color="white"
                px={4}
                alignItems="center"
                justifyContent="space-between"
                zIndex={1200}
                boxShadow="sm"
            >
                <Image
                    src="/assets/fiscalizer_white.png"
                    alt="Fiscalizer Logo"
                    h="50px"
                    maxW="180px"
                    objectFit="contain"
                    cursor="pointer"
                    onClick={() => navigateTo('/dashboard')}
                />
                <IconButton
                    aria-label="Abrir menu"
                    icon={<FaBars />}
                    variant="ghost"
                    color="white"
                    _hover={{ bg: "teal.600" }}
                    onClick={onOpen}
                />
            </Box>

            <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
                <DrawerOverlay />
                <DrawerContent bg="teal.500" color="white">
                    <DrawerCloseButton mt={1} color="white" />
                    <DrawerBody p={4} pt={12}>
                        <VStack spacing={0} align="center" minH="100%">
                            <Image
                                src="/assets/fiscalizer_white.png"
                                alt="Fiscalizer Logo"
                                onClick={() => navigateTo('/dashboard')}
                                style={{ width: '90%', marginBottom: '30px', cursor: 'pointer' }}
                            />
                            {menuButtons}
                        </VStack>
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            <Box
                bg="teal.500"
                color="white"
                position="fixed"
                h="100vh"
                w="250px"
                p={4}
                display={{ base: "none", md: "flex" }}
                flexDirection="column"
                alignItems="center"
            >
                <Image
                    src="/assets/fiscalizer_white.png"
                    alt="Fiscalizer Logo"
                    onClick={() => navigateTo('/dashboard')}
                    style={{ width: '100%', marginBottom: '15px', cursor: 'pointer' }}
                />
                {menuButtons}
            </Box>
        </>
    );
};

export default Sidebar;
